// ---------------------------------------------------------------------------
// Dialog, ported from the design system (R7)
//
// <sdr-dialog>: a small Lit element with light DOM, because a dialog has
// effects — it takes focus when it opens, keeps it inside, and gives it back.
//
//   - First focus: the element marked data-autofocus, else the body's first
//     field, else the footer's action — in a critical dialog its Cancel, the
//     first footer button. Never the close button. It is picked once the Lit
//     elements in the body have drawn (drawInside), as React commits the
//     whole tree before the system's effect: the field an <sdr-checkbox> or
//     an <sdr-multi-select> draws is there (R10d, seen in Chrome).
//   - Tab and Shift+Tab stay inside; Escape, a click on the backdrop and the
//     close button call onClose, unless dismissible is false.
//   - On close, focus goes back to what had it when the dialog opened.
//     A mousedown on the backdrop is prevented, or the browser would then
//     move the focus to the page (R7b). The system's Dialog lacks this line;
//     it is proposed for its next version.
//   - tone 'critical' makes it an alertdialog with the alert icon.
//
// The system's props map to properties, three of them renamed because the
// element would otherwise shadow its own HTML attributes: title → heading,
// children → content, className → dialogClass. `inline` is the system's
// preview mode (the dialog sits in the page and takes no focus).
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type PropertyValues } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { button } from './button.js';
import { cx, drawInside, noBox, node, present } from './helpers.js';
import { icon } from './icons.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let instances = 0;

export class SdrDialog extends LitElement {
  static override properties = {
    open: { type: Boolean },
    heading: { attribute: false },
    description: { attribute: false },
    content: { attribute: false },
    footer: { attribute: false },
    tone: {},
    size: {},
    dismissible: { type: Boolean },
    inline: { type: Boolean },
    dialogClass: { attribute: false },
    onClose: { attribute: false },
  };

  declare open: boolean;
  /** A question that names the object ("Launch Q4 Logistics DACH?"). */
  declare heading: unknown;
  /** When it takes effect and how to undo it. */
  declare description: unknown;
  declare content: unknown;
  /** Cancel first, the action last. */
  declare footer: unknown;
  declare tone: 'default' | 'critical';
  declare size: 'sm' | 'md' | 'lg';
  /** false: only the footer closes it (Escape, the backdrop and the close button do nothing). */
  declare dismissible: boolean;
  declare inline: boolean;
  declare dialogClass: string | undefined;
  declare onClose: (() => void) | undefined;

  private readonly ids = `sdr-dialog-${++instances}`;
  private opener: Element | null = null;
  private listening = false;

  constructor() {
    super();
    this.open = false;
    this.tone = 'default';
    this.size = 'md';
    this.dismissible = true;
    this.inline = false;
  }

  override createRenderRoot() {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this);
  }

  private get canDismiss(): boolean {
    return this.dismissible !== false && !!this.onClose;
  }

  private readonly onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.canDismiss) {
      event.stopPropagation();
      this.onClose?.();
      return;
    }
    if (event.key !== 'Tab' || this.inline) return;
    const root = this.querySelector('.sdr-dialog');
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;
    const at = document.activeElement;
    if (event.shiftKey && (at === first || !root.contains(at))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (at === last || !root.contains(at))) {
      event.preventDefault();
      first.focus();
    }
  };

  protected override updated(changed: PropertyValues<this>) {
    if (!changed.has('open')) return;
    if (this.open) this.activate();
    else if (changed.get('open')) this.deactivate();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.listening) this.deactivate();
  }

  private activate() {
    this.opener = document.activeElement;
    if (!this.inline) {
      drawInside(this);
      const root = this.querySelector<HTMLElement>('.sdr-dialog');
      const foot = this.querySelectorAll<HTMLElement>('.sdr-dialog__foot button:not([disabled])');
      const target =
        this.querySelector<HTMLElement>('[data-autofocus]') ||
        this.querySelector<HTMLElement>(
          '.sdr-dialog__body input:not([disabled]), .sdr-dialog__body select:not([disabled]), .sdr-dialog__body textarea:not([disabled])',
        ) ||
        (this.tone === 'critical' ? foot[0] : foot[foot.length - 1]);
      (target || root)?.focus();
    }
    document.addEventListener('keydown', this.onKeydown);
    this.listening = true;
  }

  private deactivate() {
    document.removeEventListener('keydown', this.onKeydown);
    this.listening = false;
    const opener = this.opener as HTMLElement | null;
    this.opener = null;
    if (!this.inline && opener?.focus && document.contains(opener)) opener.focus();
  }

  private onBackdrop(event: MouseEvent) {
    if (event.target !== event.currentTarget) return;
    // The browser moves the focus on mousedown, after this handler: to the page, as the backdrop takes none.
    // That would undo the focus going back to the opener, or take it out of a dialog that stays open.
    event.preventDefault();
    if (this.canDismiss) this.onClose?.();
  }

  override render() {
    if (!this.open) return nothing;
    const critical = this.tone === 'critical';
    const titleId = `${this.ids}-title`;
    const descId = `${this.ids}-desc`;
    return html`<div
      class=${cx('sdr-scrim', this.inline && 'sdr-scrim--inline')}
      @mousedown=${(e: MouseEvent) => this.onBackdrop(e)}
    >
      <div
        tabindex="-1"
        role=${critical ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby=${titleId}
        aria-describedby=${ifDefined(present(this.description) ? descId : undefined)}
        class=${cx('sdr-dialog', `sdr-dialog--${this.size || 'md'}`, critical && 'sdr-dialog--critical', this.dialogClass)}
      >
        <header class="sdr-dialog__head">
          ${critical ? icon({ name: 'alert', size: 20, className: 'sdr-dialog__icon' }) : null}
          <div class="sdr-dialog__titles">
            <h2 id=${titleId} class="sdr-dialog__title">${node(this.heading)}</h2>
            ${present(this.description) ? html`<p id=${descId} class="sdr-dialog__desc">${node(this.description)}</p>` : null}
          </div>
          ${
            this.canDismiss
              ? button({
                  variant: 'ghost',
                  size: 'sm',
                  icon: 'close',
                  ariaLabel: 'Close',
                  onClick: () => this.onClose?.(),
                })
              : null
          }
        </header>
        ${present(this.content) ? html`<div class="sdr-dialog__body">${node(this.content)}</div>` : null}
        ${present(this.footer) ? html`<footer class="sdr-dialog__foot">${node(this.footer)}</footer>` : null}
      </div>
    </div>`;
  }
}

if (!customElements.get('sdr-dialog')) customElements.define('sdr-dialog', SdrDialog);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-dialog': SdrDialog;
  }
}
